import { Fragment, Suspense } from 'react';
import { NodeProps } from 'reactflow';
import * as Combobox from '@givebutter/primitives/combobox';
import { Loader } from '@givebutter/primitives/loader';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faArrowRight, faHashtag, faPercent } from '@fortawesome/pro-regular-svg-icons';
import { faPenField } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    DateRangeValues,
    SetCustomFieldFormValues,
    SetCustomFieldNodeData,
} from 'assets/js/pages/engage/workflows/actions/types';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import { DeleteCanvasNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DeleteCanvasNodeMenuItem';
import { DuplicateNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DuplicateNodeMenuItem';
import * as NodeBadges from 'assets/js/pages/engage/workflows/components/NodeBadges';
import { StepFormContainer } from 'assets/js/pages/engage/workflows/components/StepFormContainer';
import { useCustomFieldFilterItems } from 'assets/js/pages/engage/workflows/hooks/useCustomFieldFilterItems';
import { useDebouncedAutosave } from 'assets/js/pages/engage/workflows/hooks/useDebouncedAutosave';
import { useSyncFormToNode } from 'assets/js/pages/engage/workflows/hooks/useSyncFormToNode';
import { useTriggerValidationOnDeselection } from 'assets/js/pages/engage/workflows/hooks/useTriggerValidationOnDeselection';
import { getActionNodeHeaderDetails } from 'assets/js/pages/engage/workflows/utils/getActionNodeHeaderDetails';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import { WorkflowActionTypeEnum } from 'constants/workflows';
import { DateTime } from 'luxon';
import { FilterCustomField } from 'types/domain';

interface SetCustomFieldActionNodeProps extends NodeProps<SetCustomFieldNodeData> {}
const SetCustomField = ({ id, selected, data: { formData } }: SetCustomFieldActionNodeProps) => {
    const methods = Form.useGivebutterForm<SetCustomFieldFormValues>({
        mode: 'all',
        defaultValues: {
            fieldId: formData?.values.fieldId || '',
            fieldType: formData?.values.fieldType || '',
            fieldValue: formData?.values.fieldValue || '',
        },
    });
    const {
        control,
        formState: { errors },
        watch,
        path,
        trigger,
        getValues,
    } = methods;
    const isError = Object.keys(errors).length > 0;

    const [fieldId] = watch(['fieldId']);

    const { data: customFields } = useCustomFieldFilterItems();
    const field = customFields?.find(field => field.id.toString() === fieldId);

    useSyncFormToNode<SetCustomFieldFormValues>({ control, nodeId: id });
    useDebouncedAutosave<SetCustomFieldFormValues>({ methods });
    useTriggerValidationOnDeselection<SetCustomFieldFormValues>({ trigger, selected });

    return (
        <Form.Provider {...methods}>
            <StepFormContainer>
                <CanvasNode.Root
                    className="max-w-96"
                    isSelected={selected}
                    isError={isError}
                    id={id}
                >
                    <CanvasNode.Container>
                        <CanvasNode.ErrorAlert />
                        <CanvasNode.Header>
                            <CanvasNode.Icon icon={faPenField} className="text-green-500" />
                            <CanvasNode.Title>Set custom field value</CanvasNode.Title>
                            <DropdownMenu.Root>
                                <CanvasNode.ActionMenuTrigger />
                                <DropdownMenu.Content align="end">
                                    <DuplicateNodeMenuItem />
                                    <DeleteCanvasNodeMenuItem />
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </CanvasNode.Header>
                        <CanvasNode.Body>
                            <CanvasNode.BodySelected>
                                <Form.Container>
                                    <Form.Label name={path('fieldId')}>Custom field</Form.Label>
                                    <Suspense fallback={<Loader variant="static" />}>
                                        <CustomFieldSelect />
                                    </Suspense>
                                    <Form.Error name={path('fieldId')} />
                                </Form.Container>

                                {field && (
                                    <Form.Container>
                                        <Form.Label name={path('fieldValue')}>
                                            Custom field answer
                                        </Form.Label>
                                        <CustomFieldResponseInputs field={field} key={field?.id} />
                                    </Form.Container>
                                )}
                            </CanvasNode.BodySelected>
                            <CanvasNode.BodyUnselected>
                                <SetCustomFieldFormValuesDisplay values={getValues()} />
                            </CanvasNode.BodyUnselected>
                        </CanvasNode.Body>
                    </CanvasNode.Container>
                </CanvasNode.Root>
            </StepFormContainer>
        </Form.Provider>
    );
};

interface SetCustomFieldFormValuesDisplayProps {
    values: SetCustomFieldFormValues;
    showLabel?: boolean;
}
const SetCustomFieldFormValuesDisplay = ({
    values,
    showLabel = false,
}: SetCustomFieldFormValuesDisplayProps) => {
    const { fieldId, fieldValue } = values;
    const actionHeader = getActionNodeHeaderDetails(WorkflowActionTypeEnum.SET_CUSTOM_FIELD);
    const { data: customFields } = useCustomFieldFilterItems();
    const field = customFields?.find(field => field.id.toString() === fieldId);

    if (!actionHeader || !field) return null;

    return (
        <div className="flex flex-col gap-2">
            {showLabel ? (
                <p className="text-sm font-medium text-gray-900">{actionHeader.text}</p>
            ) : null}

            <p className="text-sm font-medium">{typeof field === 'string' ? field : field.title}</p>
            <CustomFieldResponseDisplay fieldValue={fieldValue} />
        </div>
    );
};

const CustomFieldResponseDisplay = ({
    fieldValue,
}: {
    fieldValue: SetCustomFieldFormValues['fieldValue'];
}) => {
    if (typeof fieldValue === 'string' || typeof fieldValue === 'number') {
        return <p className="text-sm text-gray-500">{fieldValue}</p>;
    }

    if (typeof fieldValue === 'boolean') {
        return <p className="text-sm text-gray-500">{fieldValue ? 'Yes' : 'No'}</p>;
    }

    if (Array.isArray(fieldValue)) {
        return (
            <NodeBadges.Root>
                {fieldValue.map((value: string, index) => (
                    <Fragment key={value}>
                        <NodeBadges.Badge className="flex items-center gap-1.5 text-gray-900">
                            {value}
                        </NodeBadges.Badge>
                        {fieldValue.length > index + 1 ? (
                            <NodeBadges.Separator>AND</NodeBadges.Separator>
                        ) : null}
                    </Fragment>
                ))}
            </NodeBadges.Root>
        );
    }

    return (
        <p className="text-sm text-gray-500">
            {fieldValue.start} to {fieldValue.end}
        </p>
    );
};

const CustomFieldSelect = () => {
    const { control, setValue } = Form.useGivebutterFormContext<SetCustomFieldFormValues>();
    const { data: customFields } = useCustomFieldFilterItems();

    return (
        <Form.SingleSelect
            control={control}
            name="fieldId"
            rules={{ required: 'Please select a custom field' }}
            onValueChange={value => {
                const field = customFields?.find(field => field.id.toString() === value);
                if (field) {
                    setValue('fieldType', field.type);

                    switch (field.type) {
                        case 'checkbox':
                            setValue('fieldValue', false);
                            break;
                        case 'multiselect':
                            setValue('fieldValue', []);
                            break;
                        case 'daterange':
                            setValue('fieldValue', { start: '', end: '' });
                            break;
                        default:
                            setValue('fieldValue', '');
                    }
                }
            }}
        >
            <SingleSelect.Trigger aria-label="Custom field" />
            <SingleSelect.Content>
                {customFields?.map(value => (
                    <SingleSelect.Item value={value.id.toString()} key={value.id}>
                        <SingleSelect.ItemText>{value.title}</SingleSelect.ItemText>
                    </SingleSelect.Item>
                ))}
            </SingleSelect.Content>
        </Form.SingleSelect>
    );
};

const CustomFieldResponseInputs = ({ field }: { field?: FilterCustomField }) => {
    const { control, path } = Form.useGivebutterFormContext<SetCustomFieldFormValues>();

    if (!field) return;

    switch (field.type) {
        case 'text':
            return (
                <Form.UseFormTextInputRoot
                    name="fieldValue"
                    control={control}
                    rules={{
                        required: 'Please enter a value',
                    }}
                >
                    <Form.TextInputForm />
                </Form.UseFormTextInputRoot>
            );
        case 'textarea':
            return (
                <Form.UseFormTextArea
                    rows={5}
                    name="fieldValue"
                    control={control}
                    rules={{
                        required: 'Please enter a value',
                    }}
                />
            );
        case 'number':
        case 'percentage':
            return (
                <>
                    <Form.UseFormTextInputRoot
                        name="fieldValue"
                        control={control}
                        rules={{
                            required: 'Please enter a value',
                        }}
                        inputRootProps={{
                            mask: 'whole-number',
                        }}
                    >
                        <Form.TextInputForm />
                        <FontAwesomeIcon
                            icon={field.type === 'percentage' ? faPercent : faHashtag}
                            className={'text-gray-600'}
                        />
                    </Form.UseFormTextInputRoot>
                    <Form.Error name={path('fieldValue')} />
                </>
            );
        case 'date':
            return (
                <>
                    <Form.TextInputRoot
                        control={control}
                        name="fieldValue"
                        rules={{
                            validate: value => {
                                if (typeof value === 'string') {
                                    const date = DateTime.fromFormat(value, 'LL/dd/y');
                                    if (!date.isValid) {
                                        return 'Please enter a valid date';
                                    }
                                }
                                return true;
                            },
                        }}
                        inputRootProps={{
                            mask: 'date',
                        }}
                    >
                        <Form.TextInputForm placeholder={'MM/DD/YYYY'} />
                    </Form.TextInputRoot>
                    <Form.Error name={path('fieldValue')} />
                </>
            );
        case 'daterange':
            return (
                <div className={'grid grid-cols-11'}>
                    <div className={'col-span-5 space-y-0.5'}>
                        <Form.TextInputRoot
                            control={control}
                            name={`fieldValue.start`}
                            rules={{
                                validate: (value, form) => {
                                    if (typeof value === 'string') {
                                        const date = DateTime.fromFormat(value, 'LL/dd/y');
                                        if (!date.isValid) {
                                            return 'Please enter a valid date';
                                        }
                                    }

                                    if (
                                        (form.fieldValue as DateRangeValues).end.length &&
                                        !value.length
                                    ) {
                                        return `${field.title} needs a start and end date.`;
                                    }

                                    return true;
                                },
                            }}
                            inputRootProps={{
                                mask: 'date',
                            }}
                        >
                            <Form.TextInputForm placeholder={'MM/DD/YYYY'} />
                        </Form.TextInputRoot>
                        <Form.Error name={`fieldValue.start`} />
                    </div>
                    <div className={'col-span-1 flex justify-center pt-3'}>
                        <FontAwesomeIcon icon={faArrowRight} />
                    </div>
                    <div className={'col-span-5 space-y-0.5'}>
                        <Form.TextInputRoot
                            control={control}
                            name={`fieldValue.end`}
                            rules={{
                                validate: (value, form) => {
                                    if (typeof value === 'string') {
                                        const date = DateTime.fromFormat(value, 'LL/dd/y');
                                        if (!date.isValid) {
                                            return 'Please enter a valid date';
                                        }
                                    }

                                    if (
                                        (form.fieldValue as DateRangeValues).start.length &&
                                        !value.length
                                    ) {
                                        return `${field.title} needs a start and end date.`;
                                    }

                                    return true;
                                },
                            }}
                            inputRootProps={{
                                mask: 'date',
                            }}
                        >
                            <Form.TextInputForm placeholder={'MM/DD/YYYY'} />
                        </Form.TextInputRoot>
                        <Form.Error name={`fieldValue.end`} />
                    </div>
                </div>
            );
        case 'checkbox':
            return (
                <div>
                    <Form.UseFormCheckboxInput control={control} name="fieldValue" />
                </div>
            );

        case 'select':
            return (
                <>
                    <Form.SingleSelect
                        control={control}
                        name="fieldValue"
                        rules={{ required: 'Please select a value' }}
                    >
                        <SingleSelect.Trigger aria-label="Field value" />
                        <SingleSelect.Content>
                            {field.answers?.map(answer => (
                                <SingleSelect.Item value={answer} key={answer}>
                                    <SingleSelect.ItemText>{answer}</SingleSelect.ItemText>
                                </SingleSelect.Item>
                            ))}
                        </SingleSelect.Content>
                    </Form.SingleSelect>
                    <Form.Error name="fieldValue" />
                </>
            );
        case 'multiselect':
            return (
                <>
                    <Form.Combobox
                        control={control}
                        name="fieldValue"
                        rules={{
                            required: 'Please select a value',
                        }}
                    >
                        <Combobox.Trigger aria-label="Select values">
                            <Combobox.Tags placeholder="Select values" />
                        </Combobox.Trigger>
                        <Combobox.Popover>
                            <Combobox.Search />
                            <Combobox.Items>
                                {field.answers?.map(answer => (
                                    <Combobox.Item key={answer} value={answer}>
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>{answer}</Combobox.ItemText>
                                    </Combobox.Item>
                                ))}
                            </Combobox.Items>
                        </Combobox.Popover>
                    </Form.Combobox>
                    <Form.Error name="fieldValue" />
                </>
            );

        default:
            return null;
    }
};

export { SetCustomField, SetCustomFieldFormValuesDisplay };
export type { SetCustomFieldActionNodeProps };
